import { AddNewController, EditController } from "../../../secure/list-management/scripts/list-management.controllers";
import { ManuscriptsEditControllerConfig } from "./edit-manuscripts.objects";
import { ManuscriptsTable } from "./edit-manuscripts.table";
import { Section, SectionField } from "../../../secure/list-management/scripts/list-management.form-sections";
import { JsonItem, Manuscript } from "../../../secure/list-management/scripts/list-management.objects";

// ---------- Shared helpers (HTML5 patterns + PMID validation) ----------
function wireHtml5Patterns(modal: JQuery) {
    // DOI (allow a–z)
    modal.find('#add-ms-doi, #edit-ms-doi')
        .attr('pattern', '^10\\.[0-9]{4,9}\\/\\S+$')
        .attr('title', 'Invalid DOI. Example: 10.1000/xyz123');

    // PMID (digits only)
    modal.find('#add-ms-pmid, #edit-ms-pmid')
        .attr('pattern', '^\\d+$')
        .attr('inputmode', 'numeric')
        .attr('title', 'PMID must be digits only (e.g., 12345678)');

    // PMCID (digits-only for the numeric part; we show helper text with PMC prefix)
    modal.find('#add-ms-pmcid, #edit-ms-pmcid')
        .attr('pattern', '^\\d+$')
        .attr('title', 'PMCID must be digits only (e.g., 1234567)');

    // Anticipated Date: enforce date picker
    modal.find('#add-ms-anticipated-date, #edit-ms-anticipated-date')
        .attr('type', 'date')
        .attr('placeholder', 'YYYY-MM-DD')
        //.prop('required', true) 
        .attr('title', 'Anticipated Date is required.');

}


/// Status-based PMID requirement: if Status = Published -> PMID required + digits-only 
function validatePmid(modal: JQuery, statusSel: string, pmidSel: string): boolean {
    const $status = modal.find(statusSel);
    const $pmid = modal.find(pmidSel);

    const statusVal = String($status.val() ?? '');
    const pl: any[] = (modal.data('pl-ms-status') as any[]) ?? [];
    const resolved = pl.find(x => String(x.Id) === statusVal)?.Value;
    const isPublished =
        (resolved ?? $status.find('option:selected').text())
            .toString().trim().toLowerCase() === 'published';
    togglePmidVisibility(modal, pmidSel, isPublished);
   

    // Toggle HTML5 required
    $pmid.prop('required', isPublished);

    // Normalize pasted input
    const raw = String($pmid.val() ?? '').trim();
    const digitsOnly = raw.replace(/\D+/g, '');
    if (raw !== digitsOnly) $pmid.val(digitsOnly);

    const ok = !isPublished || (digitsOnly.length > 0 && /^\d+$/.test(digitsOnly));

    // Error state only when required + invalid
    const invalid = isPublished && !ok;
    $pmid.toggleClass('is-invalid', invalid).attr('aria-invalid', invalid ? 'true' : null);
    if (invalid) { ($pmid[0] as HTMLInputElement | undefined)?.reportValidity?.(); }

    return ok;
}

function attachPmidValidation(modal: JQuery, statusSel: string, pmidSel: string) {
    modal
        .off(`change.pmid${pmidSel} input.pmid${pmidSel}`)
        .on(`change.pmid${pmidSel}`, statusSel, () => validatePmid(modal, statusSel, pmidSel))
        .on(`input.pmid${pmidSel}`, pmidSel, () => validatePmid(modal, statusSel, pmidSel));

    validatePmid(modal, statusSel, pmidSel);
}
function validateRequiredDate(modal: JQuery, selector: string): boolean {
    const $input = modal.find(selector);

    const raw = String($input.val() ?? "").trim();

    // Placeholder sentinel
    const isEmpty = !raw || raw === "0001-01-01";


    $input.toggleClass("is-invalid", isEmpty)
        .toggleClass("is-valid", !isEmpty);

    return !isEmpty;
}



// ---------- Shared helper ----------
function applySortedPickListDropdown(
    modal: JQuery,
    selectId: string,
    pickListId: string,
    sortedPickLists: JsonItem[] // or any[] if your sortedPickLists isn't JsonItem-typed
): void {
    const list = sortedPickLists?.find(p => p.Id === pickListId)?.Json ?? [];
    const $sel = modal.find(selectId);
    if ($sel.length === 0 || !list.length) return;

    // preserve current selection safely (works pre/post selectpicker init)
    const currentVal = $sel.val();

    const ordered = [...list].sort((a: any, b: any) => (a.SortOrder ?? 0) - (b.SortOrder ?? 0));

    $sel.empty();
    ordered.forEach((x: any) => {
        $sel.append(new Option(x.Value, x.Id.toString(), false, false));
    });

    // refresh if bootstrap-select is active
    try { ($sel as any).selectpicker("refresh"); } catch { /* ignore */ }

    // restore selection
    if (currentVal != null) {
        $sel.val(currentVal);
        try { ($sel as any).selectpicker("val", currentVal); } catch { /* ignore */ }
    }
}

const normalizeDateToIso = (v: any): string | null => {
    const raw = String(v ?? '').trim();
    if (!raw) return null;

    // If already YYYY-MM-DD, make it ISO midnight
    if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return `${raw}T00:00:00`;

    const d = new Date(raw);
    return isNaN(d.getTime()) ? null : d.toISOString();
};

function togglePmidVisibility(modal: JQuery, pmidSel: string, show: boolean) {
    const $pmid = modal.find(pmidSel);

    const $wrap = $pmid.closest('.form-group, .row, .section-field, .mb-3').first();

    $wrap.toggleClass('d-none', !show);

    if (!show) {
        $pmid.prop('required', false).removeClass('is-invalid').attr('aria-invalid', null);
        
    }
}



//============= EDIT=====================
export class EditManuscriptsController {
    // Config variables
    submitRoute: string;
    modalIdTarget: string;
    isAdmin: boolean;
    tableVarName: string;
    parentTable: ManuscriptsTable;

    // Const Varaibles
    id: string;
    title: string;
    sections: Section[];
    displayPropertyName: string;
    referenceId: string = "ms";

    editController: EditController;

    constructor(config: ManuscriptsEditControllerConfig, parent: ManuscriptsTable) {
        this.id = "manuscripts-edit-controller";
        this.title = "Edit Manuscript";
        this.submitRoute = config.submitRoute;
        this.modalIdTarget = config.modalIdTarget;
        this.isAdmin = config.isAdmin;
        this.tableVarName = config.tableVarName;
        this.parentTable = parent;

        this.displayPropertyName = "Title";

        this.sections = [
            // 1) Overview
            new Section("edit-ms-general-section", "Overview", [
                new SectionField("edit-ms-title", "Title", "input-lg", "Title", true, ""),
             
                new SectionField("edit-ms-center", "Primary Center", "dropdown-pl", "CenterId", true, "pl-center-limited"),

                new SectionField(
                    "edit-ms-core",
                    "Core(s)",
                    "filtered-multi-dropdown-pl",
                    "CoreIds",
                    false,
                    "pl-core",
                    "",
                    [["edit-ms-center", "CenterId"]],
                    ["pl-all-cores", "Id"]
                ),
                new SectionField(
                    "edit-ms-project",
                    "Project(s)",
                    "filtered-multi-dropdown-pl",
                    "ProjectIds",
                    false,
                    "pl-project",
                    "",
                    [["edit-ms-center", "CenterId"]],
                    ["pl-all-projects", "Id"]
                ),

                new SectionField("edit-ms-collab-centers", "Collaborating Center(s)", "multi-dropdown-pl", "CollaboratingCenterIds", false, "pl-center"),
                new SectionField("edit-ms-notes", "Notes", "input-lg", "Notes", false, "", "Optional notes.")
            ]),

            // 2) Authors 
            new Section("edit-ms-authors-section", "Authors", [
                new SectionField("edit-ms-primary-author", "Primary Author (i.e., First Author)​", "dropdown-pl-search", "PrimaryAuthorId", true, "pl-user"),
                new SectionField("edit-ms-senior-author", "Senior Author (i.e., Last Author)​", "dropdown-pl-search", "SeniorAuthorId", true, "pl-user"),
                new SectionField("edit-ms-network-authors", "Additional Network Member Authors​", "multi-dropdown-pl-search", "NetworkAuthorIds", false, "pl-user")
            ]),

            // 3) Publication IDs
            new Section("edit-ms-pubids-section", "Status & Publication Info", [
                new SectionField("edit-ms-status", "Status", "dropdown-pl", "StatusId", true, "pl-ms-status"),
                new SectionField("edit-ms-pmid", "PMID", "input", "PubMedId", false, "", "Example: 12345678"),
                new SectionField("edit-ms-target-journal", "Target Journal", "input-lg", "TargetJournal", false, "", "If targeting a journal, list it here."),
                new SectionField("edit-ms-anticipated-date", "Anticipated Date", "date-input", "AnticipatedDate", true, "", "Enter anticipated submission or publication date."),

            ])
        ];

        this.editController = new EditController(this.id, this.title, "", this.sections, this.modalIdTarget, this.displayPropertyName);
    }

    initialize(pickLists: JsonItem[], selectedItems: any[], selectedSectionId: string): void {
        this.editController.initialize(pickLists, selectedItems, selectedSectionId);


        const $modal = $(`#${this.modalIdTarget}`);
      
        $modal.find(".is-valid, .is-invalid")
            .removeClass("is-valid is-invalid");

        // Cache status picklist for ID-> Value resolution in PMID validation
        $modal.data('pl-ms-status', (pickLists.find(p => p.Id === 'pl-ms-status')?.Json ?? []));
        applySortedPickListDropdown($modal, "#edit-ms-status", "pl-ms-status", this.parentTable.sortedPickLists);

        // Wire HTML5 patterns + bubbles
        wireHtml5Patterns($modal);
    
        attachPmidValidation($modal, '#edit-ms-status', '#edit-ms-pmid');
       
        $modal
            .find('#edit-ms-doi, #edit-ms-pmid, #edit-ms-pmcid')
            .off('input.validation')
            .on('input.validation', e => (e.currentTarget as HTMLInputElement).reportValidity?.());
        // Footer button
        const htmlFooterContent =
            `<button class="greenbtn" type="button" onclick="page.${this.tableVarName}.saveEditModal()">Save & Close</button>` +
            `<span class="error-msg ml-5 text-danger font-weight-500" id="request-footer-error"></span>`;
        $modal.find(".modal-footer").empty().html(htmlFooterContent);
    }
    async save(): Promise<any> {
        if (this.editController.selectedSection.fields.some(field => field.specialCaseEntity != undefined)) {
            this.editController.updateChangeLog();
        }

        return await $.ajax(this.submitRoute, {
            method: "POST",
            headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
            data: { data: JSON.stringify(this.editController.updatedItems) } // important
        });
    }

    

    showLoading(isLoading: boolean) {
        if (isLoading) {
            this.showSaved(false);
            $("#edit-item-content").addClass("d-none");
            $("#request-item-saving").removeClass("d-none");
        }
        else {
            $("#edit-item-content").removeClass("d-none");
            $("#request-item-saving").addClass("d-none");
        }
    }

    showSaved(isSaved: boolean) {
        if (isSaved) {
            $("#request-save-complete-block").removeClass("d-none");
            $("#request-save-in-progress-block").addClass("d-none");
        }
        else {
            $("#request-save-complete-block").addClass("d-none");
            $("#request-save-in-progress-block").removeClass("d-none");
        }
    }

    validateChanged(): boolean {
        let notChangedMessage = "";

        this.editController.selectedSection.fields.forEach(field => {
            if (!field.checkChanged()) {
                notChangedMessage = "Please update the Manuscript information."
            }
        });

        let errorMsgTarget = $("#request-footer-error")
        errorMsgTarget.text(notChangedMessage);

        return notChangedMessage === "";
    }

    private additionalValidation(): boolean {
  
        const $modal = $(`#${this.modalIdTarget}`);
        $modal.find("#request-footer-error").text("");
        return true;
    }

    getSelectedFilterTabId(): string {
        const selectedTab = $(`#${this.referenceId}-filter-tabs`).find(".active").first();
        return selectedTab.prop("id");
    }

    async saveEditModal(): Promise<void> {
       
        const $modal = $(`#${this.modalIdTarget}`);

        const rawDateVal = $modal.find("#edit-ms-anticipated-date").val();
        const dateFieldExists = $modal.find("#edit-ms-anticipated-date").length > 0;

        // Force paint on all fields in current section
        this.editController.selectedSection.fields.forEach(f => f.validate());

        // compute + paint date AFTER so it can't be overwritten
        const dateOk = !dateFieldExists || validateRequiredDate($modal, "#edit-ms-anticipated-date");
        if (!dateOk) {
            $modal.find("#request-footer-error").text("Please complete all required fields.");
            return;
        }
     
        // Base validation
       
        let isValid = this.editController.validate();
     
        if (!isValid) {
         
            const pmidState = validatePmid($modal, '#edit-ms-status', '#edit-ms-pmid');

            $modal.find("#request-footer-error")
                .text("Please complete all required fields.");
            return;
        }

        // Enforce PMID when Published
        const pmidOk = validatePmid($modal, '#edit-ms-status', '#edit-ms-pmid');
       

        if (!pmidOk) {
          
            $modal.find("#request-footer-error").text(
                "When Status is Published, a valid numeric PubMed ID is required."
            );
            return;
        }

        const additionalOk = this.additionalValidation();
 
        this.editController.showLoading(true);

        const selectedTab: string = this.getSelectedFilterTabId();
        const currentFilterOptions: any[][] =
            this.parentTable.filterTabs.map(tab => tab.getSelectedValues());
        const sortOrder: any[] = this.parentTable.getCurrentTableSort();

        const items = this.editController.updatedItems as Manuscript[];
     

        if (!items || !items.length) {
          
            $modal.find("#request-footer-error").text("No changes detected to save.");
            this.editController.showLoading(false);
            return;
        }

        (items[0] as any).LastUpdated = new Date().toISOString();

        items.forEach(m => {
            const before = (m as any).AnticipatedDate;
            const normalized = normalizeDateToIso(before);  
            (m as any).AnticipatedDate = normalized;
        });

        try {
            const result: any[] = await this.save();
           
        } catch (err) {
           
            this.editController.showLoading(false);
            return;
        }

        await this.parentTable.initialize();
        this.editController.showSaved(true);

        for (let i = 0; i < currentFilterOptions.length; i++) {
            this.parentTable.filterTabs[i].setSelectedValues(currentFilterOptions[i]);
        }

        $(`#${selectedTab}`).trigger("click");
        this.parentTable.setCurrentTableSort(sortOrder);

        const editedItems: Manuscript[] = this.editController.updatedItems;
        if (editedItems?.length) {
          
            this.parentTable.scrollToManuscript(editedItems[0].Id);
        }

       
        setTimeout(() => {
            $(`#${this.modalIdTarget}`).modal('hide');
            setTimeout(() => {
                this.editController.showLoading(false);
                this.editController.showSaved(false);
            }, 1000);
        }, 1000);
    }


}
// ======================
// === ADD ============
// ======================
export class AddManuscriptsController {
    // Config variables
    submitRoute: string;
    modalIdTarget: string;
    isAdmin: boolean;
    tableVarName: string;
    parentTable: ManuscriptsTable;

    // Const Varaibles
    id: string;
    title: string;
    sections: Section[];
    displayPropertyName: string;
    referenceId: string = "ms";

    addController: AddNewController;

    constructor(config: ManuscriptsEditControllerConfig, parent: ManuscriptsTable) {
        this.id = "manuscript-add-controller";
        this.submitRoute = config.submitRoute;
        this.modalIdTarget = config.modalIdTarget;
        this.isAdmin = config.isAdmin;
        this.tableVarName = config.tableVarName;
        this.parentTable = parent;

        this.displayPropertyName = "Title";
        this.title = "Add New In Development Manuscript​";

        this.sections = [
            // 1) Overview
            new Section("ms-general-section", "Overview", [
                new SectionField("add-ms-title", "Title", "input-lg", "Title", true, ""),
                new SectionField("add-ms-center", "Primary Center", "dropdown-pl", "CenterId", true, "pl-center-limited"),

                new SectionField(
                    "add-ms-core",
                    "Core(s)",
                    "filtered-multi-dropdown-pl",
                    "CoreIds",
                    false,
                    "pl-core",
                    "",
                    [["add-ms-center", "CenterId"]],
                    ["pl-all-cores", "Id"]
                ),
                new SectionField(
                    "add-ms-project",
                    "Project(s)",
                    "filtered-multi-dropdown-pl",
                    "ProjectIds",
                    false,
                    "pl-project",
                    "",
                    [["add-ms-center", "CenterId"]],
                    ["pl-all-projects", "Id"]
                ),

                new SectionField("add-ms-collab-centers", "Collaborating Center(s)", "multi-dropdown-pl", "CollaboratingCenterIds", false, "pl-center"),
                new SectionField("add-ms-notes", "Notes", "input-lg", "Notes", false, "", "Optional Notes.")
            ]),

            // 2) Authors
            new Section("ms-authors-section", "Authors", [
                new SectionField("add-ms-primary-author", "Primary Author (i.e., First Author)​", "dropdown-pl-search", "PrimaryAuthorId", true, "pl-user"),
                new SectionField("add-ms-senior-author", "Senior Author (i.e., Last Author)​", "dropdown-pl-search", "SeniorAuthorId", true, "pl-user"),
                new SectionField("add-ms-network-authors", "Additional Network Member Authors​", "multi-dropdown-pl-search", "NetworkAuthorIds", false, "pl-user")
            ]),

            // 3) Publication IDs
            new Section("ms-pubids-section", "Status & Publication Info", [
                new SectionField("add-ms-status", "Status", "dropdown-pl", "StatusId", true, "pl-ms-status"),
                new SectionField("add-ms-pmid", "PMID", "input", "PubMedId", false, "", "Example: 12345678"),
                new SectionField("add-ms-target-journal", "Target Journal", "input-lg", "TargetJournal", false, ""),
                new SectionField("add-ms-anticipated-date", "Anticipated Date", "date-input", "AnticipatedDate", true, "", "Enter anticipated submission or publication date."),

                
            ])
        ];

        this.addController = new AddNewController(this.id, this.title, "", this.sections, this.modalIdTarget);
    }

    initialize(pickLists: JsonItem[], defaultItem: any, description: string = ""): void {
        this.addController.initialize(pickLists, defaultItem);

        const modal = $(`#${this.modalIdTarget}`);


        modal.data('pl-ms-status', (pickLists.find(p => p.Id === 'pl-ms-status')?.Json ?? []));
        applySortedPickListDropdown(modal, "#add-ms-status", "pl-ms-status", this.parentTable.sortedPickLists);

        // Patterns + live bubbles
        wireHtml5Patterns(modal);
       

        modal
            .find('#add-ms-doi, #add-ms-pmid, #add-ms-pmcid')
            .off('input.validation')
            .on('input.validation', e => (e.currentTarget as HTMLInputElement).reportValidity?.());
        attachPmidValidation(modal, '#add-ms-status', '#add-ms-pmid');

        // Title + footer
        let htmlTitleContent = `<div>${this.title}</div>`;
        if (description) {
            htmlTitleContent = `
        <div>${this.title}</div>
        <div class="font-size14 font-italic font-weight-500">${description}</div>
      `;
        }
        modal.find(".modal-title").empty().html(htmlTitleContent);

        const htmlFooterContent =
            `<button class="greenbtn" type="button" onclick="page.${this.tableVarName}.saveAddModal()">Add Manuscript</button>` +
            `<span class="error-msg ml-5 text-danger font-weight-500" id="request-footer-error"></span>`;
        modal.find(".modal-footer").empty().html(htmlFooterContent);
    }

    async save(): Promise<any> {
        const updatedItem = this.addController.getCurrentItem();
        (updatedItem as any).AnticipatedDate =
            normalizeDateToIso((updatedItem as any).AnticipatedDate);

        let result: any;
        try {
            result = await $.ajax(this.submitRoute, {
                method: "POST",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                data: { data: JSON.stringify([updatedItem]) }
            });

            if (this.addController.fileUploadFields.length > 0) {
                for (let i = 0; i < this.addController.fileUploadFields.length; i++) {
                    let fileUploadField = this.addController.fileUploadFields[i];
                    await fileUploadField.uploadAttachments(result);
                }
            }
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    showLoading(isLoading: boolean): void {
        this.addController.showLoading(isLoading);
    }

    showSaved(isSaved: boolean): void {
        this.addController.showSaved(isSaved);
    }

    getSelectedFilterTabId(): string {
        const selectedTab = $(`#${this.referenceId}-filter-tabs`).find(".active").first();
        return selectedTab.prop("id");
    }

    private additionalValidation(): boolean {
        this.updateErrorText("");
        return true;
    }

    private updateErrorText(errorMsg: string = "") {
        const errorBlock = $(`#${this.modalIdTarget}`).find("#request-footer-error");
        errorBlock.text(errorMsg);
    }

    async saveAddModal(): Promise<void> {
        const modal = $(`#${this.modalIdTarget}`);
        modal.find('#add-ms-anticipated-date').trigger('blur').trigger('change');

        // 1) Paint ALL validation states (no early returns)
        const baseOk = this.addController.validate(); // paints controller-required fields
        const dateOk = validateRequiredDate(modal, "#add-ms-anticipated-date"); // paints date
        const pmidOk = validatePmid(modal, '#add-ms-status', '#add-ms-pmid'); // paints pmid (and toggles required)

        // 2) Decide
        const allOk = baseOk && dateOk && pmidOk;

        if (!allOk) {
            this.updateErrorText(
                !pmidOk
                    ? "When Status is Published, a valid numeric PubMed ID is required."
                    : "Please complete all required fields."
            );
            return;
        }

        if (!this.additionalValidation()) return;

        // Save + table refresh
        this.addController.showLoading(true);

        const selectedTab: string = this.getSelectedFilterTabId();
        const currentFilterOptions = this.parentTable.filterTabs.map(tab => tab.getSelectedValues());
        const sortOrder = this.parentTable.getCurrentTableSort();

        const result: any[] = await this.save();
        await this.parentTable.initialize();

        this.addController.showSaved(true);

        for (let i = 0; i < currentFilterOptions.length; i++) {
            this.parentTable.filterTabs[i].setSelectedValues(currentFilterOptions[i]);
        }
        $(`#${selectedTab}`).trigger("click");
        this.parentTable.setCurrentTableSort(sortOrder);
        this.parentTable.scrollToManuscript();

        setTimeout(() => {
            $(`#${this.modalIdTarget}`).modal('hide');
            setTimeout(() => {
                this.addController.showLoading(false);
                this.addController.showSaved(false);
            }, 1000);
        }, 1000);
    }

}

